import { useEffect, useRef, useState } from "react";
import { CreacionSalidaApi } from "../api/CreacionSalidaApi";
import { NotificationManager } from "react-notifications";
import { getErrorFromResponse } from "../../../../../../../../utility/utils";
import { useForm } from "react-hook-form";
import moment from "moment";
import { TIPOS_DESTINO_BODEGA_PT } from "../../../../../../../../utility/constants";
import { formatPalletLabel } from "../../../../../ProduccionV2/helpers/formatPallet";
import { useFechaHoraReloj } from "../../../../../ProduccionV2/hooks/useFechaHoraReloj";
import { CONTEXTO_RELOJ_BODEGA } from "../../../../../ProduccionV2/components/RelojDesfase/contextosReloj";
import { tipoExistenciaSegunDestino } from "../helpers/filtroExistencias";

export const useSalidaForm = (navigateToBoleta) => {
    const {
        crearSalida,
        getAlmacenesReproceso,
        getPalletsIngresadosDespues,

        cargando,
    } = CreacionSalidaApi();

    // la salida se registra con la fecha y hora del reloj de bodega
    const relojBodega = useFechaHoraReloj(CONTEXTO_RELOJ_BODEGA);

    const [detallesSalida, setDetallesSalida] = useState([]);
    const [detallesSobrante, setDetallesSobrante] = useState([]);

    const {
        control: formControl,
        register: formRegister,
        setValue: formSetValue,
        getValues: formGetValues,
        trigger: validateForm,
        watch: formWatch,
        formState: { errors: formErrors },
    } = useForm({
        mode: "onChange",
        defaultValues: {
            fecha: moment(),
        },
    });

    const tipoDestinoSeleccionado = formWatch("tipo_destino");
    const esReproceso =
        tipoDestinoSeleccionado &&
        tipoDestinoSeleccionado.value === TIPOS_DESTINO_BODEGA_PT.REPROCESO;

    // listado completo de almacenes de reproceso disponibles, compartido por
    // los formularios de pallets y de sobrantes, usado para autoseleccionar
    // el que corresponde al padre PT del producto elegido
    const [almacenesReprocesoDisponibles, setAlmacenesReprocesoDisponibles] =
        useState([]);

    useEffect(() => {
        if (!esReproceso) return;

        getAlmacenesReproceso({}).then(setAlmacenesReprocesoDisponibles);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [esReproceso]);

    // pallets que no aparecen en las existencias porque ingresaron a bodega
    // después de la fecha y hora del reloj de bodega. Se cuentan según el
    // destino, que define la conformidad que se puede despachar
    const tipoDestinoValor =
        tipoDestinoSeleccionado && tipoDestinoSeleccionado.value;
    const [palletsOcultos, setPalletsOcultos] = useState([]);
    const consultaPalletsOcultos = useRef(0);

    useEffect(() => {
        consultaPalletsOcultos.current += 1;
        const consulta = consultaPalletsOcultos.current;

        setPalletsOcultos([]);

        if (!tipoDestinoValor) return;

        getPalletsIngresadosDespues({
            tipo: tipoExistenciaSegunDestino(tipoDestinoValor),
        }).then((pallets) => {
            if (consulta !== consultaPalletsOcultos.current) return;
            setPalletsOcultos(pallets);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tipoDestinoValor]);

    const FormSubmit = async () => {
        const valid = await validateForm();
        if (!valid) return;

        // si no hay detalles de salida, mostrar advertencia
        if (detallesSalida.length === 0 && detallesSobrante.length === 0) {
            NotificationManager.warning(
                "Debe agregar al menos un producto a despachar",
                "Advertencia",
                3000,
            );
            return;
        }

        const formValues = formGetValues();
        const tipoDestino =
            formValues.tipo_destino && formValues.tipo_destino.value;
        const esReclasificacion =
            tipoDestino === TIPOS_DESTINO_BODEGA_PT.RECLASIFICACION;
        const esReproceso = tipoDestino === TIPOS_DESTINO_BODEGA_PT.REPROCESO;

        // al enviar a reclasificación, cada pallet debe llevar su tarjeta amarilla;
        // los detalles agregados mientras el destino era otro no la tienen
        if (esReclasificacion) {
            const detallesSinTarjeta = detallesSalida.filter(
                (detalle) => !detalle.tarjeta_salida,
            );

            if (detallesSinTarjeta.length > 0) {
                const pallets = detallesSinTarjeta
                    .map((detalle) =>
                        formatPalletLabel(
                            detalle.numero_pallet,
                            detalle.identificador_fisico,
                        ),
                    )
                    .join(", ");

                NotificationManager.warning(
                    `Los siguientes pallets no tienen tarjeta amarilla asignada: ${pallets}. Elimínelos y agréguelos nuevamente.`,
                    "Advertencia",
                    3000,
                );
                return;
            }

            const sobrantesSinTarjeta = detallesSobrante.filter(
                (detalle) => !detalle.tarjeta_salida,
            );

            if (sobrantesSinTarjeta.length > 0) {
                const productos = sobrantesSinTarjeta
                    .map((detalle) => detalle.producto)
                    .join(", ");

                NotificationManager.warning(
                    `Los siguientes sobrantes no tienen tarjeta amarilla asignada: ${productos}. Elimínelos y agréguelos nuevamente.`,
                    "Advertencia",
                    3000,
                );
                return;
            }
        }

        // al enviar a reproceso, cada pallet debe llevar su almacén de reproceso destino;
        // los detalles agregados mientras el destino era otro no lo tienen
        if (esReproceso) {
            const detallesSinBodegaDestino = detallesSalida.filter(
                (detalle) => !detalle.bodega_destino_id,
            );

            if (detallesSinBodegaDestino.length > 0) {
                const pallets = detallesSinBodegaDestino
                    .map((detalle) =>
                        formatPalletLabel(
                            detalle.numero_pallet,
                            detalle.identificador_fisico,
                        ),
                    )
                    .join(", ");

                NotificationManager.warning(
                    `Los siguientes pallets no tienen almacén de reproceso asignado: ${pallets}. Elimínelos y agréguelos nuevamente.`,
                    "Advertencia",
                    3000,
                );
                return;
            }

            const sobrantesSinBodegaDestino = detallesSobrante.filter(
                (detalle) => !detalle.bodega_destino_id,
            );

            if (sobrantesSinBodegaDestino.length > 0) {
                const productos = sobrantesSinBodegaDestino
                    .map((detalle) => detalle.producto)
                    .join(", ");

                NotificationManager.warning(
                    `Los siguientes sobrantes no tienen almacén de reproceso asignado: ${productos}. Elimínelos y agréguelos nuevamente.`,
                    "Advertencia",
                    3000,
                );
                return;
            }
        }

        const body = {
            tipo_salida: formValues.tipo_salida && formValues.tipo_salida.value,
            tipo_destino:
                formValues.tipo_destino && formValues.tipo_destino.value,
            observaciones: formValues.observaciones,
            detalles: detallesSalida.map((detalle) => ({
                existencia_id: detalle.existencia_id,
                pacas: detalle.pacas,
                tarjeta_salida: esReclasificacion
                    ? detalle.tarjeta_salida
                    : null,
                bodega_destino_id: esReproceso
                    ? detalle.bodega_destino_id
                    : null,
            })),
            detalles_sobrante: detallesSobrante.map((detalle) => ({
                existencia_id: detalle.existencia_id,
                kg_secos: detalle.kg_secos,
                tarjeta_salida: esReclasificacion
                    ? detalle.tarjeta_salida
                    : null,
                bodega_destino_id: esReproceso
                    ? detalle.bodega_destino_id
                    : null,
            })),
        };

        await CrearSalida(body);
    };

    const CrearSalida = async (body) => {
        try {
            // Lógica para guardar la gestión de almacén
            const salida = await crearSalida(body);

            NotificationManager.success(
                "Salida creada con éxito",
                "Éxito",
                3000,
            );

            navigateToBoleta(salida.id);
        } catch (error) {
            const errorMsg = getErrorFromResponse(
                error,
                "Error al crear la salida",
            );
            NotificationManager.error(errorMsg, "Error", 3000);
        }
    };

    return {
        formControl,
        formRegister,
        formErrors,
        formWatch,
        formGetValues,
        formSetValue,

        detallesSalida,
        setDetallesSalida,
        detallesSobrante,
        setDetallesSobrante,
        almacenesReprocesoDisponibles,
        palletsOcultos,
        fechaHoraBodega: relojBodega.obtenerFechaHora(),
        FormSubmit,
        cargando,
    };
};
